import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { eventsApi } from '@/api/events'
import { usersApi } from '@/api/users'
import { TicketTypeManager, makeRow } from '@/components/events/TicketTypeManager'
import type { TicketTypeRowData } from '@/components/events/TicketTypeRow'
import { Spinner } from '@/components/ui/Spinner'
import { toast } from '@/store/toastStore'
import type { User } from '@/types/auth'

interface MatchRow { id: string; home_team: string; away_team: string; match_time: string }

export function EventCreatePage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)

  // Infos
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [eventDate, setEventDate] = useState('')
  const [eventTime, setEventTime] = useState('')
  const [location, setLocation] = useState('')
  const [supervisorId, setSupervisorId] = useState<number | null>(null)

  // Supervisors list
  const [supervisors, setSupervisors] = useState<User[]>([])
  useEffect(() => {
    usersApi.listSupervisors().then(setSupervisors).catch(() => {})
  }, [])

  // Types de tickets
  const [ticketTypes, setTicketTypes] = useState<TicketTypeRowData[]>([
    makeRow({ name: 'Normal', price: '2000', capacity: '0' }),
  ])
  const [ticketTypesError, setTicketTypesError] = useState('')

  // Matchs
  const [matches, setMatches] = useState<MatchRow[]>([])
  const addMatch = () =>
    setMatches((prev) => [...prev, { id: Math.random().toString(36).slice(2), home_team: '', away_team: '', match_time: '' }])
  const removeMatch = (id: string) => setMatches((prev) => prev.filter((m) => m.id !== id))
  const updateMatch = (id: string, field: keyof MatchRow, value: string) =>
    setMatches((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: value } : m)))

  const validate = () => {
    const valid = ticketTypes.filter((t) => t.name.trim() && Number(t.price) >= 0)
    if (valid.length === 0) {
      setTicketTypesError('Ajoutez au moins un type de ticket avec un nom.')
      return false
    }
    setTicketTypesError('')
    return true
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    try {
      const event = await eventsApi.create({
        name,
        description: description || undefined,
        event_date: eventDate,
        event_time: eventTime,
        location,
        supervisor_id: supervisorId,
        ticket_types: ticketTypes
          .filter((t) => t.name.trim())
          .map((t, i) => ({
            name: t.name,
            description: t.description || undefined,
            price: Number(t.price) || 0,
            capacity: Number(t.capacity) || 0,
            sort_order: i,
          })),
        matches: matches
          .filter((m) => m.home_team && m.away_team)
          .map((m, i) => ({
            home_team: m.home_team,
            away_team: m.away_team,
            match_time: m.match_time || undefined,
            sort_order: i,
          })),
      })
      toast.success('Événement créé avec succès.')
      navigate(`/events/${event.id}`)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erreur lors de la création.'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 bg-white transition-all duration-150 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500'

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/events')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Retour aux événements
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Nouvel événement</h1>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">

        {/* Section Informations */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Informations</h2>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Nom de l'événement <span className="text-danger-500">*</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="ex: Journée 5 — Championnat National" className={inputClass} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Description de l'événement..." className={`${inputClass} resize-none`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">Date <span className="text-danger-500">*</span></label>
                <input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} required className={inputClass} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700">Heure <span className="text-danger-500">*</span></label>
                <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} required className={inputClass} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Lieu <span className="text-danger-500">*</span></label>
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} required placeholder="ex: Stade Léopold Sédar Senghor, Dakar" className={inputClass} />
            </div>

            {/* Superviseur */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                </svg>
                Superviseur
                <span className="text-xs font-normal text-gray-400 ml-1">— optionnel</span>
              </label>
              {supervisors.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-2">
                  Aucun superviseur disponible.{' '}
                  <button type="button" onClick={() => navigate('/users')} className="text-primary-600 hover:underline">
                    En créer un →
                  </button>
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSupervisorId(null)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all ${
                      supervisorId === null
                        ? 'border-gray-400 bg-gray-100 text-gray-700 font-medium'
                        : 'border-gray-200 text-gray-400 hover:border-gray-300'
                    }`}
                  >
                    Aucun
                  </button>
                  {supervisors.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSupervisorId(s.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all ${
                        supervisorId === s.id
                          ? 'border-violet-400 bg-violet-50 text-violet-700 font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-violet-200 hover:bg-violet-50/40'
                      }`}
                    >
                      <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0">
                        {s.name[0].toUpperCase()}
                      </span>
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-400">
                Le superviseur pourra consulter les ventes et entrées de cet événement.
              </p>
            </div>
          </div>
        </div>

        {/* Section Types de tickets */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900">Types de tickets</h2>
            <p className="text-sm text-gray-500 mt-0.5">Définissez les catégories et leurs tarifs. Capacité 0 = illimité.</p>
          </div>
          <TicketTypeManager rows={ticketTypes} onChange={setTicketTypes} error={ticketTypesError} />
        </div>

        {/* Section Matchs */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Programme des matchs</h2>
              <p className="text-sm text-gray-500 mt-0.5">Optionnel — pour les événements sportifs.</p>
            </div>
            <button type="button" onClick={addMatch} className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
              + Ajouter
            </button>
          </div>
          {matches.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">Aucun match ajouté.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {matches.map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <input type="text" value={m.home_team} onChange={(e) => updateMatch(m.id, 'home_team', e.target.value)} placeholder="Équipe domicile" className={`${inputClass} min-w-0 flex-1`} />
                  <span className="text-gray-400 shrink-0 text-sm font-medium">vs</span>
                  <input type="text" value={m.away_team} onChange={(e) => updateMatch(m.id, 'away_team', e.target.value)} placeholder="Équipe extérieure" className={`${inputClass} min-w-0 flex-1`} />
                  <input type="time" value={m.match_time} onChange={(e) => updateMatch(m.id, 'match_time', e.target.value)} className="w-36 shrink-0 px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white transition-all duration-150 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500" />
                  <button type="button" onClick={() => removeMatch(m.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-danger-600 hover:bg-danger-50 transition-colors shrink-0">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/events')} className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            Annuler
          </button>
          <button type="submit" disabled={loading} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm transition-colors disabled:opacity-50">
            {loading ? <><Spinner className="w-4 h-4 text-white" /> Création...</> : 'Créer l\'événement'}
          </button>
        </div>

      </form>
    </div>
  )
}
